import ExpandMore from "@mui/icons-material/ExpandMore"
import {
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import isEmpty from "lodash/isEmpty"
import { CSSProperties, FC, useRef, useState } from "react"

import { convertToEnDateString } from "../../../../helpers/date"
import { useAccount } from "../../../../states/account"
import { useLocale } from "../../../../states/locale"
import { BookingWhite, BookingYellow } from "src/components/common/icon"
import { SelectOption } from "src/components/common/select"
import { getColorVariables } from "src/components/layouts/basic/BasicLayout.variables"
import { create } from "src/helpers/bem"

import { joinToInlineString } from "src/helpers/nameUtils"
import { mergeBooking, useBooking } from "src/states/bookingDetail"
import { useBookings } from "src/states/bookings"
import { mergeCarts } from "src/states/shop/carts"
import { TranslationMessages } from "src/translations"
import { xRMApiPermission, xRMApiSalesorderPhase } from "src/types/xRM"

import styles from "./StandBookingSelection.module.scss"

const bem = create(styles, "StandBookingSelection")
const style = { ...getColorVariables(undefined) }

export type StandBookingSelectionProps = {
  errorText?: string
  helperText?: string
  isShopPage?: boolean
  messages?: TranslationMessages
  onValidate?: (error?: string) => void
  showAdminOnly?: boolean
}
export const StandBookingSelection: FC<StandBookingSelectionProps> = ({
  isShopPage = false,
  messages,
  showAdminOnly = false,
}) => {
  const locale = useLocale()
  const { contactId } = useAccount()
  const [expanded, setExpanded] = useState<boolean>(false)
  const { bookings } = useBookings()
  const { booking: bookingDetail } = useBooking()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"))
  const [menuWidth, setMenuWidth] = useState<number | undefined>(undefined)

  const triggerRef = useRef<HTMLDivElement>(null)

  const allowedPhases: xRMApiSalesorderPhase[] = ["Invoicing", "Service"]

  const handleOpen = () => {
    setExpanded(true)

    if (triggerRef.current) {
      setMenuWidth(triggerRef.current.clientWidth)
    }
  }

  const filteredBookings = isShopPage
    ? bookings.filter(
        (booking) =>
          allowedPhases.includes(booking.phase) &&
          !(
            booking.permissions.length === 1 &&
            booking.permissions[0].role === "Administrator" &&
            booking.contactId !== contactId &&
            booking.senderContactId !== contactId
          ),
      )
    : showAdminOnly
      ? bookings.filter(
          (booking) =>
            booking.permissions?.some(
              (permission: xRMApiPermission) =>
                permission.role === "Administrator",
            ) ?? false,
        )
      : bookings

  const sortedBookings = [...filteredBookings].sort((a, b) => {
    const dateA = new Date(convertToEnDateString(a.createdOn, locale)).getTime()
    const dateB = new Date(convertToEnDateString(b.createdOn, locale)).getTime()
    return dateB - dateA
  })

  const items = sortedBookings.map(
    (booking) =>
      ({
        key: booking.id,
        value: `${joinToInlineString([
          booking.name,
          booking.standhall
            ? `${messages?.components.core.header.standSelection.standhall}: ${booking.standhall}`
            : undefined,
          booking.standnumber
            ? `${messages?.components.core.header.standSelection.standnumber}: ${booking.standnumber}`
            : undefined,
        ])} ${
          booking.phase === "Draft"
            ? messages?.components.core.header.standSelection.draft
            : ""
        }`,
        onclick: () => {
          const bookingId = bookings.find((el) => el.name === booking.name)?.id
          mergeCarts({
            currentCartId: bookingId?.toString() ?? "",
          })
          mergeBooking({
            id: bookingId?.toString() ?? "",
          })
        },
      }) as SelectOption,
  )

  const selectedBooking: SelectOption | undefined = bookingDetail
    ? items.find((selected) => selected.key === bookingDetail.id)
    : undefined

  return (
    <FormControl
      fullWidth
      className={bem()}
      sx={{ color: styles.color, flex: isMobile ? 1 : undefined }}
      variant="standard"
    >
      <Select
        ref={triggerRef}
        disableUnderline
        displayEmpty
        className={bem("select", expanded ? "expanded" : "")}
        IconComponent={ExpandMore}
        id="standbooking-selection"
        value={selectedBooking?.key ?? ""}
        MenuProps={{
          slotProps: {
            paper: {
              className: bem("wrapper"),
              style: {
                width: menuWidth,
              },
            },
          },
          MenuListProps: {
            className: bem("menu"),
            style: style as CSSProperties,
          },
          disableScrollLock: true,
        }}
        renderValue={(selected) => {
          if (!selectedBooking || !selected) {
            return (
              <Typography component="em">
                {isEmpty(items)
                  ? messages?.components.core.header.standSelection.noStands
                  : messages?.components.core.header.standSelection.placeholder}
              </Typography>
            )
          }
          return <>{selectedBooking?.value}</>
        }}
        startAdornment={
          <InputAdornment position="start">
            <BookingYellow className={bem("icon")} />
          </InputAdornment>
        }
        onClose={() => setExpanded(false)}
        onOpen={handleOpen}
        onChange={(event) => {
          const option = items.find((item) => item.key === event.target.value)
          if (option?.onclick) option.onclick()
        }}
      >
        {items
          .filter((item) => item.key !== selectedBooking?.key)
          .map((item) => (
            <MenuItem
              key={item.key}
              className={bem("menuitem")}
              disabled={item.disabled}
              value={item.key}
            >
              <BookingWhite className={bem("menuitem__icon")} />
              {item.value}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  )
}
